import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import api from "@/lib/api";
import { toast } from "sonner";
import { Eye, EyeOff, AlertCircle, ShieldCheck, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [valid, setValid] = useState(null); // null=loading, true/false
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setValid(false); return; }
    api.post("/auth/validate-reset-token", { token })
      .then(({ data }) => { setValid(!!data.valid); setEmail(data.email || ""); })
      .catch(() => setValid(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 6) { toast.error("A senha deve ter ao menos 6 caracteres"); return; }
    if (pwd !== confirm) { toast.error("As senhas não conferem"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao redefinir senha");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-hero-radial bg-white">
      <div className="max-w-[460px] mx-auto px-5 pt-10">
        <div className="flex justify-center mb-7"><Logo size={40} /></div>
        <Card className="rounded-2xl shadow-sm">
          <CardContent className="p-6">
            {valid === null && (
              <p className="text-sm text-muted-foreground text-center py-6">Verificando link…</p>
            )}
            {valid === false && (
              <div className="text-center py-2" data-testid="reset-invalid">
                <div className="w-14 h-14 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center mx-auto mb-3">
                  <AlertCircle className="w-7 h-7" />
                </div>
                <h2 className="font-display text-lg font-semibold text-[hsl(var(--primary))]">Link inválido ou expirado</h2>
                <p className="text-sm text-muted-foreground mt-2">
                  Este link de redefinição não é mais válido. Solicite um novo a partir da tela "Esqueci minha senha".
                </p>
                <div className="mt-5 grid grid-cols-2 gap-2">
                  <Link to="/forgot-password"><Button className="w-full" data-testid="reset-request-new">Solicitar novo</Button></Link>
                  <Button variant="outline" onClick={() => navigate("/login")}>Login</Button>
                </div>
              </div>
            )}
            {valid && !done && (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <ShieldCheck className="w-5 h-5 text-[hsl(var(--accent))]" />
                  <h1 className="font-display text-xl font-semibold text-[hsl(var(--primary))]">Definir nova senha</h1>
                </div>
                {email && <p className="text-xs text-muted-foreground mb-3">Conta: <strong className="text-foreground">{email}</strong></p>}
                <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
                  <div className="space-y-1.5">
                    <Label>Nova senha</Label>
                    <div className="relative">
                      <Input
                        type={showPwd ? "text" : "password"}
                        required
                        value={pwd}
                        onChange={(e) => setPwd(e.target.value)}
                        placeholder="Mínimo 6 caracteres"
                        data-testid="reset-new-password"
                      />
                      <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground" aria-label="Mostrar/ocultar senha">
                        {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Confirmar nova senha</Label>
                    <Input
                      type={showPwd ? "text" : "password"}
                      required
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      placeholder="Repita a senha"
                      data-testid="reset-confirm-password"
                    />
                  </div>
                  <Button type="submit" className="w-full h-11" disabled={loading} data-testid="reset-submit">
                    {loading ? "Salvando…" : "Definir nova senha"}
                  </Button>
                </form>
              </>
            )}
            {valid && done && (
              <div className="text-center py-3" data-testid="reset-success">
                <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto mb-3">
                  <CheckCircle2 className="w-7 h-7" />
                </div>
                <h2 className="font-display text-lg font-semibold text-[hsl(var(--primary))]">Senha redefinida!</h2>
                <p className="text-sm text-muted-foreground mt-2">Sua nova senha foi salva com sucesso. Você já pode fazer login.</p>
                <Button className="w-full mt-5" onClick={() => navigate("/login")} data-testid="reset-go-login">Ir para o login</Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
